export type PlanCode = "free" | "creator" | "pro" | "studio";
export type ModuleKey = "today" | "studio" | "calendar" | "clients" | "campaigns" | "money" | "insights" | "memory" | "billing";

export interface RuntimeHealth {
  status: "ready" | "configuration_required";
  services: {
    account: boolean;
    data: boolean;
    ai: boolean;
    billing: boolean;
  };
}

export interface Profile {
  id: string;
  display_name: string;
  brand_name: string;
  niche: string;
  audience: string;
  voice: string;
  goals: string;
  avoid: string;
  platforms: string[];
  onboarding_completed: boolean;
}

export interface Subscription {
  id: string;
  user_id: string;
  plan: PlanCode;
  status: "free" | "trialing" | "active" | "past_due" | "canceled" | "paused";
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  ai_generations_used: number;
}

export interface TaskRecord {
  id: string;
  user_id: string;
  title: string;
  context: string;
  due_date: string | null;
  completed: boolean;
}

export interface ClientRecord {
  id: string;
  user_id: string;
  name: string;
  contact_name: string;
  contact_email: string;
  preferred_channel: string;
  relationship_stage: string;
  notes: string;
}

export interface CampaignRecord {
  id: string;
  user_id: string;
  client_id: string | null;
  name: string;
  status: string;
  fee_minor: number;
  currency: string;
  due_date: string | null;
  deliverables: string;
  required_claims: string;
  prohibited_claims: string;
  disclosure: string;
  usage_rights: string;
  exclusivity: string;
  revision_cap: number | null;
  payment_terms: string;
  attention: string;
  brief_text: string;
}

export interface ContentRecord {
  id: string;
  user_id: string;
  campaign_id: string | null;
  title: string;
  platform: string;
  format: string;
  status: string;
  planned_at: string | null;
  body: string;
  source: string;
  externally_published: boolean;
  external_url: string | null;
}

export interface InvoiceRecord {
  id: string;
  user_id: string;
  client_id: string | null;
  campaign_id: string | null;
  reference: string;
  amount_minor: number;
  currency: string;
  due_date: string | null;
  status: string;
  received_minor: number;
  notes: string;
}

export interface MemoryRecord {
  id: string;
  user_id: string;
  kind: string;
  title: string;
  body: string;
  source: string;
  confirmed: boolean;
  excluded: boolean;
}

export interface GenerationRecord {
  id: string;
  user_id: string;
  tool: string;
  prompt: string;
  output: string;
  model: string;
  campaign_id: string | null;
  feedback: string | null;
  created_at: string;
}

export interface AttachmentRecord {
  id: string;
  user_id: string;
  campaign_id: string | null;
  blob_key: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  created_at: string;
}

export interface WorkspaceData {
  profile: Profile;
  subscription: Subscription;
  tasks: TaskRecord[];
  clients: ClientRecord[];
  campaigns: CampaignRecord[];
  content: ContentRecord[];
  invoices: InvoiceRecord[];
  memory: MemoryRecord[];
  generations: GenerationRecord[];
  attachments: AttachmentRecord[];
}

export interface PlanDefinition {
  code: PlanCode;
  name: string;
  monthly: number;
  annual: number;
  description: string;
  features: string[];
  aiLimit: number;
  recommended?: boolean;
}

export const PLANS: PlanDefinition[] = [
  {
    code: "free",
    name: "Start",
    monthly: 0,
    annual: 0,
    description: "Build the habit and bring the creator workflow into one place.",
    features: ["10 AI generations each month", "One active campaign", "Content calendar", "Client and invoice records"],
    aiLimit: 10
  },
  {
    code: "creator",
    name: "Creator",
    monthly: 19,
    annual: 190,
    description: "For independent creators handling regular content and paid work.",
    features: ["150 AI generations each month", "Unlimited active campaigns", "All creator workspace modules", "Campaign Passport fields", "Creator memory and record-based insights"],
    aiLimit: 150,
    recommended: true
  },
  {
    code: "pro",
    name: "Creator Pro",
    monthly: 39,
    annual: 390,
    description: "For full-time creators with a heavier publishing and brand calendar.",
    features: ["600 AI generations each month", "Unlimited active campaigns", "All Creator plan workspace modules", "Higher monthly creative allowance"],
    aiLimit: 600
  },
  {
    code: "studio",
    name: "Studio",
    monthly: 99,
    annual: 990,
    description: "For high-volume creators and manager-led operations needing a larger allowance.",
    features: ["2,000 AI generations each month", "Unlimited active campaigns", "All Creator Pro workspace modules", "Highest monthly creative allowance"],
    aiLimit: 2000
  }
];
